import { useEffect, useState } from 'react';
import { Box, Fab, Fade, Menu, MenuItem } from '@mui/material';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { motion } from 'framer-motion';
import { useAuthSession } from '../../contexts/AuthSessionContext';
import { navigateInApp } from '../../services/navigation';
import { acceptRestaurant } from '../../services/browserAuthRuntime';
import {
  getRestaurantes,
  switchRestaurante,
} from '../Products/Manager/services/api';

const triggerStyle = {
  position: 'fixed',
  bottom: 16,
  right: 0,
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const RestaurantSwitcher = () => {
  const { isAdmin } = useAuthSession();
  const [restaurantes, setRestaurantes] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isTriggerHovered, setIsTriggerHovered] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      setRestaurantes([]);
      return;
    }

    void loadRestaurantes();
  }, [isAdmin]);

  const loadRestaurantes = async () => {
    try {
      const data = await getRestaurantes();
      setRestaurantes(data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = async (id) => {
    try {
      const response = await switchRestaurante(id);
      acceptRestaurant(response);
      navigateInApp('/products', { replace: true });
    } catch (err) {
      console.error(err);
    } finally {
      handleClose();
    }
  };

  if (!isAdmin) return null;
  const isTriggerVisible = isTriggerHovered || Boolean(anchorEl);

  return (
    <>
      <motion.div
        style={triggerStyle}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 260, damping: 20 }}
        onMouseEnter={() => setIsTriggerHovered(true)}
        onMouseLeave={() => setIsTriggerHovered(false)}
      >
        <Fab
          color="primary"
          onClick={handleOpen}
          size="medium"
          sx={{
            transform: isTriggerVisible ? 'translateX(0)' : 'translateX(145%)',
            opacity: isTriggerVisible ? 1 : 0,
            transition: 'transform 0.25s ease, opacity 0.25s ease',
            pointerEvents: isTriggerVisible ? 'auto' : 'none',
            boxShadow: '0 4px 12px rgba(0, 123, 255, 0.3)',
            background: 'linear-gradient(45deg, #007BFF 30%, #0056b3 90%)',
            '&:hover': {
              background: 'linear-gradient(45deg, #0056b3 30%, #003d82 90%)',
              boxShadow: '0 6px 16px rgba(0, 123, 255, 0.4)',
            },
            '@media (hover: none)': {
              transform: 'translateX(0)',
              opacity: 1,
              pointerEvents: 'auto',
            },
          }}
        >
          <RestaurantIcon />
        </Fab>
        <Box
          sx={{
            width: 12,
            height: 52,
            borderRadius: '14px 0 0 14px',
            background: 'linear-gradient(180deg, #4db2ff 0%, #0a7cff 42%, #0050a8 100%)',
            border: '1px solid rgba(255, 255, 255, 0.42)',
            borderRight: 'none',
            boxShadow:
              '-8px 0 16px rgba(0, 123, 255, 0.34), inset 1px 0 0 rgba(255, 255, 255, 0.36)',
            opacity: isTriggerVisible ? 0 : 1,
            transform: isTriggerVisible ? 'translateX(8px) scaleX(0.7)' : 'translateX(0) scaleX(1)',
            transition: 'opacity 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
            pointerEvents: 'none',
            '@media (hover: none)': {
              opacity: 0,
              transform: 'translateX(8px) scaleX(0.7)',
            },
          }}
        />
      </motion.div>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        TransitionComponent={Fade}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {restaurantes.map((restaurante) => (
          <MenuItem
            key={restaurante.id}
            onClick={() => handleSelect(restaurante.id)}
          >
            {restaurante.nome}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default RestaurantSwitcher;
